import { sortBy as _sortBy } from 'lodash-es';
import { Fragment } from 'react';
import { makeStyles } from 'tss-react/mui';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import SpeedIcon from '@mui/icons-material/Speed';
import StraightenIcon from '@mui/icons-material/Straighten';
import PanToolIcon from '@mui/icons-material/PanTool';
import FilterHdrIcon from '@mui/icons-material/FilterHdr';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import { getEventCategoryName, getEventCategoryIdsForType } from '../../utils/event-categories';
import {
  eventAgeRestrictions,
  eventPaces,
  eventDistances,
  eventRegroups,
  eventTerrains,
  eventTypes,
  eventWeatherCancellations,
} from '../../utils/events';
import type { Event } from '../../types/Event';

const useStyles = makeStyles()((theme) => ({
  wrapper: {
    display: 'flex',
    flexFlow: 'row wrap',
    margin: theme.spacing(2, 0, 1, 0),
  },
  tag: {
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
    '&:last-child': {
      marginRight: 0,
    },
  },
  icon: {
    marginRight: theme.spacing(0.5),
    fontSize: 16,
  },
  iconChip: {
    maxWidth: '100%',
    backgroundColor: theme.palette.action.hover,
    '& > span': {
      display: 'inline-flex',
      alignItems: 'center',
    },
  },
  rideStatChip: {
    maxWidth: '100%',
    backgroundColor: '#fff',
    '& > span': {
      display: 'inline-flex',
      alignItems: 'center',
    },
  },
  tagChip: {
    backgroundColor: theme.palette.action.hover,
    border: '1px solid rgba(0, 0, 0, 0.06)',
  },
}));

type Props = {
  event?: Event;
  showRideStats?: boolean;
};

const EventCategoryTags = ({ event, showRideStats = true }: Props) => {
  const { classes } = useStyles();

  const {
    ageRestriction,
    eventType,
    categoryIds,
    loop,
    pace,
    distance,
    terrain,
    regroup,
    weatherCancellation,
  } = event;

  const hasAgeRestriction = ageRestriction && ageRestriction !== 'GENERAL';
  const hasCategories = categoryIds && categoryIds.length > 0;
  const hasRideStats = showRideStats && (eventType || pace || distance || terrain || loop || regroup || weatherCancellation || hasAgeRestriction);

  if (!hasCategories && !hasRideStats) {
    return null;
  }

  const EventTypeIcon = eventType ? eventTypes[eventType].icon : null;
  const categoryIdsForType = getEventCategoryIdsForType(eventType);
  const sortedCategoryIds = _sortBy(categoryIds, (categoryId) => (
    categoryIdsForType.indexOf(categoryId)
  ));

  return (
    <div className={classes.wrapper}>
      {showRideStats && eventType && EventTypeIcon && (
        <span className={classes.tag}>
          <Chip
            label={(
              <Fragment>
                <EventTypeIcon fontSize="small" className={classes.icon} />
                {eventTypes[eventType].name}
              </Fragment>
            )}
            variant="outlined"
            size="small"
            className={classes.rideStatChip}
          />
        </span>
      )}
      {showRideStats && pace && (
        <span className={classes.tag}>
          <Chip
            label={(
              <Fragment>
                <SpeedIcon fontSize="small" className={classes.icon} />
                {eventPaces[pace].name} ({eventPaces[pace].description})
              </Fragment>
            )}
            variant="outlined"
            size="small"
            className={classes.rideStatChip}
          />
        </span>
      )}
      {showRideStats && distance && (
        <span className={classes.tag}>
          <Chip
            label={(
              <Fragment>
                <StraightenIcon fontSize="small" className={classes.icon} />
                {eventDistances[distance].name}
              </Fragment>
            )}
            variant="outlined"
            size="small"
            className={classes.rideStatChip}
          />
        </span>
      )}
      {showRideStats && terrain && (
        <Tooltip title={eventTerrains[terrain].description}>
          <span className={classes.tag}>
            <Chip
              label={(
                <Fragment>
                  <FilterHdrIcon fontSize="small" className={classes.icon} />
                  {eventTerrains[terrain].name}
                </Fragment>
              )}
              variant="outlined"
              size="small"
              className={classes.rideStatChip}
            />
          </span>
        </Tooltip>
      )}
      {showRideStats && regroup && (
        <span className={classes.tag}>
          <Chip
            label={(
              <Fragment>
                <PanToolIcon fontSize="small" className={classes.icon} />
                {eventRegroups[regroup].name}
              </Fragment>
            )}
            variant="outlined"
            size="small"
            className={classes.iconChip}
          />
        </span>
      )}
      {showRideStats && weatherCancellation && (
        <span className={classes.tag}>
          <Chip
            label={(
              <Fragment>
                <WbSunnyIcon fontSize="small" className={classes.icon} />
                {eventWeatherCancellations[weatherCancellation].name}
              </Fragment>
            )}
            variant="outlined"
            size="small"
            className={classes.iconChip}
          />
        </span>
      )}
      {showRideStats && loop && (
        <span className={classes.tag}>
          <Chip
            label={(
              <Fragment>
                <AllInclusiveIcon fontSize="small" className={classes.icon} />
                Loop
              </Fragment>
            )}
            variant="outlined"
            size="small"
            className={classes.iconChip}
          />
        </span>
      )}
      {showRideStats && hasAgeRestriction && (
        <Tooltip title={eventAgeRestrictions[ageRestriction].description}>
          <span className={classes.tag}>
            <Chip
              label={(
                <Fragment>
                  <SupervisedUserCircleIcon fontSize="small" className={classes.icon} />
                  {eventAgeRestrictions[ageRestriction].name}
                </Fragment>
              )}
              variant="outlined"
              size="small"
              className={classes.iconChip}
            />
          </span>
        </Tooltip>
      )}
      {sortedCategoryIds.map((categoryId) => (
        <span key={categoryId} className={classes.tag}>
          <Chip
            label={getEventCategoryName(categoryId)}
            size="small"
            className={classes.tagChip}
          />
        </span>
      ))}
    </div>
  );
};

export default EventCategoryTags;
